import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import $ from 'jquery';
import { Typography, makeStyles, Radio } from '@material-ui/core';
import { ServiceOfferDetails } from 'models';
import { useLanguage, useAuth, useListing } from 'hooks';
import { useIntl } from 'react-intl';
import { hideModal } from 'state/actions';
import { initialize } from 'state/slices/aiServices';
import { COLOR, SUBSCRIPTION, MODAL } from 'config/constants';
import { HvButton, HvBeforeAfterSlider } from 'components';

interface ServiceSelectionProps {
  services: ServiceOfferDetails[];
  onServiceSelect: (service: any) => void;
  onClose: () => void;
}

const useStyles = makeStyles({
  service: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 8,
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
    borderRadius: 5,
  },
  buttonBox: {
    padding: 5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  leftButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  warningContent: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    '@media (min-width: 767px)': {
      minWidth: 400,
    },
    alignItems: 'center',
    minHeight: 200,
  },
  link: {
    display: 'inline',
    color: COLOR.LIGHT_BLUE,
    textDecoration: 'underline',
  },
});

function ServiceSelection({ services, onServiceSelect, onClose }: ServiceSelectionProps) {
  const language = useLanguage();
  const classes = useStyles();
  const intl = useIntl();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const path = window.location.pathname;
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { listing } = useListing(listing_token);

  const [isPremiumAccount, setIsPremiumAccount] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceOfferDetails>(services[0] || null);
  const [radioButtonChecked, setRadioButtonChecked] = useState(services[0] ? services[0].id : 0);
  const onRadioButtonClick = (service: ServiceOfferDetails) => {
    setRadioButtonChecked(service.id);
    setSelectedService(service);
  };
  const fmtPricePerPictures = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.pricePerPhoto',
  });
  const fmtNext = intl.formatMessage({ id: 'next' });
  const fmtCancel = intl.formatMessage({ id: 'cancel' });
  const fmtPleaseGoTo = intl.formatMessage({ id: 'deliver.video_slideshow.pleaseGoTo' });
  const fmtProfilePage = intl.formatMessage({ id: 'deliver.video_slideshow.profilePage' });
  const fmtChangeSubscription = intl.formatMessage({
    id: 'deliver.aiServices.changeSubscription',
  });
  function onNextClick() {
    onServiceSelect(selectedService);
  }

  useEffect(() => {
    const isPremium =
      [
        SUBSCRIPTION.plateforme,
        SUBSCRIPTION.application,
        SUBSCRIPTION.carte,
        SUBSCRIPTION.base,
      ].includes(listing.broker.subscription_id) || user.isAdmin;
    setIsPremiumAccount(isPremium);
  }, [listing]);

  const checkAndSetSize = (beforePicture: any, afterPicture: any) => {
    const modalWidth = $('#aiServicesModal').eq(0).width();
    const bodyWidth = window.innerWidth;
    const normalizedWidth = Math.min(beforePicture.width, afterPicture.width);
    const normalizedHeight = Math.min(beforePicture.height, afterPicture.height);
    const aspectRatio = normalizedHeight / normalizedWidth;
    let width = bodyWidth > 1000 ? normalizedWidth : modalWidth;
    width = width <= 750 ? width : 750;
    const height = width * aspectRatio;
    return { width, height };
  };
  if (!services.length || !isPremiumAccount) {
    return (
      <div className={classes.warningContent}>
        <Typography>
          <span>{`${fmtPleaseGoTo} `}</span>
          <span>
            <a
              onClick={() => {
                dispatch(hideModal(MODAL.AI_SERVICES));
                dispatch(initialize());
                window.open('/profile', '_blank');
              }}
            >
              <span className={classes.link}>{fmtProfilePage}</span>
            </a>
          </span>
          <span>{` ${fmtChangeSubscription}`}</span>
        </Typography>
      </div>
    );
  }
  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <HvBeforeAfterSlider
          afterImage={selectedService.example.after_image_url}
          beforeImage={selectedService.example.before_image_url}
          checkAndSetSize={checkAndSetSize}
        />
      </div>
      {services.length > 0 &&
        services.map((service: ServiceOfferDetails) => {
          const serviceObj = new ServiceOfferDetails(service);
          return (
            <div className={classes.service} key={service.id}>
              <div>
                <Typography>{serviceObj.getDisplayName(language)}</Typography>
                <Typography style={{ fontSize: 14 }}>
                  {`${serviceObj.price}$ ${fmtPricePerPictures}`}
                </Typography>
              </div>
              <Radio
                disableRipple
                onClick={() => {
                  onRadioButtonClick(serviceObj);
                }}
                color='secondary'
                checked={radioButtonChecked === serviceObj.id}
              />
            </div>
          );
        })}
      <div className={classes.buttonBox}>
        <HvButton
          onClick={() => {
            onClose();
          }}
          className={classes.leftButton}
        >
          {fmtCancel}
        </HvButton>
        <HvButton
          onClick={() => {
            onNextClick();
          }}
          disabled={!selectedService || !isPremiumAccount}
        >
          {fmtNext}
        </HvButton>
      </div>
    </div>
  );
}

export default ServiceSelection;
